import React from 'react';
import { MultiColorIcon } from '@tt/ui';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { useRemoveRenterPaymentMethod } from '../../../pages/payments/usePayments';
import paymentMethodPropTypes from '../../../prop-types/payment-method-prop-types';
import { useConfig } from '../../../providers/ConfigProvider';
import Button from '../../Button';
import Modal from '../../Modal';
import { useErrorToast, useSuccessToast } from '../../Toast';
import { H2 } from '../../Typography';

import styles from './DeletePaymentMethodModal.module.scss';

const DeletePaymentMethodModal = ({
  open,
  onCancel,
  onDelete,
  paymentMethod,
}) => {
  const [removePaymentMethod, { loading: removeLoading }] =
    useRemoveRenterPaymentMethod();

  const showSuccessToast = useSuccessToast();
  const showErrorToast = useErrorToast();
  const { PRIVATE_BASE_PATH } = useConfig();

  const handleDelete = async (pm) => {
    try {
      await removePaymentMethod({
        variables: { id: pm.id },
      });
      showSuccessToast(`${pm.brand} ending in ${pm.last4} was deleted.`);
      onDelete();
    } catch (e) {
      showErrorToast(e.message);
    }
  };

  if (!paymentMethod) {
    return;
  }

  const { preferred_for_rent_reporting, preferred_for_auto_pay } =
    paymentMethod;

  const states = {
    autopay: {
      buttonText: 'Update Autopay',
      message:
        'This payment method cannot be removed because it is being used for autopay.',
      icon: true,
      path: `${PRIVATE_BASE_PATH}settings/autopay`,
      onClick: () => {},
      buttonVariant: 'primary',
      textAlign: 'center',
    },
    rent_reporting: {
      buttonText: 'Update Rent Reporting',
      message:
        'This payment method cannot be removed because it is being used for rent reporting.',
      icon: true,
      path: `${PRIVATE_BASE_PATH}settings/rent-reporting`,
      onClick: () => {},
      buttonVariant: 'primary',
      textAlign: 'center',
    },
    delete: {
      buttonText: 'Delete',
      message: `Are you sure you want to delete ${paymentMethod.brand} ending in ${paymentMethod.last4}?`,
      icon: false,
      onClick: () => handleDelete(paymentMethod),
      buttonVariant: 'danger',
      textAlign: 'left',
    },
  };

  function getReason() {
    if (preferred_for_rent_reporting) {
      return states.rent_reporting;
    }
    if (preferred_for_auto_pay) {
      return states.autopay;
    }
    return states.delete;
  }

  const { buttonText, message, icon, path, onClick, buttonVariant, textAlign } =
    getReason();

  return (
    <Modal
      disableOutsideClick={removeLoading}
      disableCloseIcon={removeLoading}
      showCloseIcon
      open={open}
      size="small"
      onClose={onCancel}
    >
      <Modal.Content noHeader className={styles.content}>
        {icon && <MultiColorIcon name="warning" size="xl" color="error" />}
        <H2 className={cx('m-0', { 'mt-24': icon })} align={textAlign}>
          {message}
        </H2>
      </Modal.Content>
      <Modal.Footer className={styles.footer}>
        <Button
          small
          variant="secondary"
          fullWidth
          onClick={onCancel}
          disabled={removeLoading}
          className={styles.button}
        >
          Cancel
        </Button>
        <Button
          small
          fullWidth
          variant={buttonVariant}
          loading={removeLoading}
          to={path}
          onClick={onClick}
          className={styles.button}
        >
          {buttonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DeletePaymentMethodModal.propTypes = {
  open: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  paymentMethod: paymentMethodPropTypes.isRequired,
};

export default DeletePaymentMethodModal;
